import { useEffect, useState } from 'react'
import { useAuthContext } from 'shared/components/AuthContext'
import {
  getNewestAuthTime,
  getSessionExpiresAt,
  shareAuthTime,
  useViewerMode,
} from 'shared/utils'

// Undefined until the auth time is read from the token. Loaded once, by
// SessionExpiresAtProvider - read it with useSessionExpiresAt.
export const useLoadSessionExpiresAt = () => {
  const { user, auth } = useAuthContext()
  const { mode } = useViewerMode()
  const [sessionExpiresAt, setSessionExpiresAt] = useState<number>()

  useEffect(() => {
    setSessionExpiresAt(undefined)
    if (!user) return

    let cancelled = false
    let latestLoadId = 0

    const loadSessionExpiresAt = () => {
      const loadId = ++latestLoadId

      user
        .getIdTokenResult()
        .then(({ authTime }) => {
          // A load started later may have read a newer token
          if (cancelled || loadId !== latestLoadId) return

          shareAuthTime(user.uid, authTime)
          setSessionExpiresAt(
            getSessionExpiresAt(getNewestAuthTime(user.uid, authTime), mode)
          )
        })
        // Retried when the user comes back to the tab
        .catch(() => undefined)
    }

    const loadSessionExpiresAtWhenVisible = () => {
      if (document.visibilityState === 'visible') loadSessionExpiresAt()
    }

    // Signing in again, in this or another tab, gives the token a new auth time.
    // Firebase updates the same user object then, so the effect isn't rerun.
    loadSessionExpiresAt()
    const unsubscribeFromIdTokenChanges =
      auth?.onIdTokenChanged(loadSessionExpiresAt)
    window.addEventListener('focus', loadSessionExpiresAt)
    document.addEventListener(
      'visibilitychange',
      loadSessionExpiresAtWhenVisible
    )

    return () => {
      cancelled = true
      unsubscribeFromIdTokenChanges?.()
      window.removeEventListener('focus', loadSessionExpiresAt)
      document.removeEventListener(
        'visibilitychange',
        loadSessionExpiresAtWhenVisible
      )
    }
  }, [auth, mode, user])

  return sessionExpiresAt
}
